import React, { useState, useEffect } from "react";
import {
  addDoc,
  collection,
  updateDoc,
  serverTimestamp,
  getDoc,
  doc,
} from "firebase/firestore";
import { db } from "../../../firebase"; // Ensure the path is correct
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Grid,
  Avatar,
  FormControlLabel,
  Paper,
} from "@mui/material";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import DynamicUploadModal from "../../../components/UploadModal";
import { useLocation, useNavigate } from "react-router-dom"; // Import useNavigate

import AccessControlManager from "../../../components/AccessControlManager";

const AddNewClinic = () => {
  const [openModal, setOpenModal] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(""); // State to store the uploaded URL
  const [loading, setLoading] = useState(true);

  // Function to parse query parameters
  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };

  const navigate = useNavigate(); // Initialize navigate
  const query = useQuery();
  const uid = query.get("uid"); // Extracting uid from query parameters

  useEffect(() => {
    const fetchData = async () => {
      try {
        const docRef = doc(db, "clinic", uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setClinicData(docSnap.data());
        } else {
          console.log("No document found");
        }
      } catch (error) {
        console.error("Error fetching document:", error);
      } finally {
        setLoading(false);
      }
    };
    if (uid) fetchData();
  }, [uid]);

  const handleModalClose = () => {
    setOpenModal(false);
  };

  const handleUploadSuccess = (url) => {
    console.log("Image uploaded successfully! URL:", url);
    setUploadedImageUrl(url); // Set the uploaded URL to the state
    setClinicData((prevData) => ({
      ...prevData,
      imgurl: url, // Set the uploaded URL in the clinicData
    }));
  };

  const [clinicData, setClinicData] = useState({
    name: "",
    address: "",
    altphone: "",
    description: "",
    country: "",
    state: "",
    district: "",
    phone: "",
    pincode: "",
    rating: 0,
    regNo: "",
    redirectUrl: "",
    imgurl: "",
    open_date_time: {
      Monday: { firstHalf: "", secondHalf: "", open: false },
      Tuesday: { firstHalf: "", secondHalf: "", open: false },
      Wednesday: { firstHalf: "", secondHalf: "", open: false },
      Thursday: { firstHalf: "", secondHalf: "", open: false },
      Friday: { firstHalf: "", secondHalf: "", open: false },
      Saturday: { firstHalf: "", secondHalf: "", open: false },
      Sunday: { firstHalf: "", secondHalf: "", open: false },
    },
    lat: "",
    lng: "",
    is_clinic: false,
    is_hospital: false,
    is_online: false,
    is_oxygen: false,
    is_lab: false,
    is_diagnostic: false, // Added diagnostic checkbox
    doctors: [],
    specialist: [],
    timestamp: serverTimestamp(),
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setClinicData((prevData) => ({
      ...prevData,
      [name]: name === "rating" ? Number(value) : value,
      timestamp: serverTimestamp(),
    }));
  };

  const handleToggle = (field) => {
    setClinicData((prevData) => ({
      ...prevData,
      [field]: !prevData[field],
    }));
  };

  const handleOpenDateTimeChange = (day, field, value) => {
    setClinicData((prevData) => ({
      ...prevData,
      open_date_time: {
        ...prevData.open_date_time,
        [day]: {
          ...prevData.open_date_time[day],
          [field]: value,
        },
      },
    }));
  };

  const handleAddClinic = async () => {
    try {
      const clinicDataWithTimestamp = {
        ...clinicData,
        timestamp: serverTimestamp(),
      };
      const docRef = await addDoc(
        collection(db, "clinic"),
        clinicDataWithTimestamp
      );
      await updateDoc(docRef, { id: docRef.id });
      setSnackbarMessage("Clinic added successfully!");
      setSnackbarSeverity("success");
      setClinicData({ ...clinicData });
    } catch (error) {
      console.error("Error adding clinic:", error);
      setSnackbarMessage("Failed to add clinic.");
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true);
    }
  };

  const handleUpdateClinic = async () => {
    if (!uid) return;
    try {
      const clinicRef = doc(db, "clinic", uid);
      await updateDoc(clinicRef, { ...clinicData });
      setSnackbarMessage("Clinic updated successfully!");
      setSnackbarSeverity("success");
    } catch (error) {
      console.error("Error updating clinic:", error);
      setSnackbarMessage("Failed to update clinic.");
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true);
    }
  };

  const handleUpdateLabTest = async () => {
    navigate(`/my-lab?id=${uid}`);
  };

  return (
    <Box sx={{ padding: 2, width: "80%", margin: "auto" }}>
      <Avatar
        style={{
          width: 100,
          height: 100,
          margin: "0 auto",
          marginBottom: 20,
        }}
      >
        {clinicData.imgurl ? (
          <img
            src={clinicData.imgurl}
            alt="Category"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "50%",
            }}
          />
        ) : (
          <PhotoCamera style={{ fontSize: 50, color: "#2058C8FF" }} />
        )}
      </Avatar>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
        }}
      >
        <AccessControlManager documentId={uid} />
      </Box>
      {clinicData.is_lab ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleUpdateLabTest}
          >
            Add LabTest
          </Button>
        </Box>
      ) : (
        <></>
      )}

      <Typography sx={{ fontSize: 16, color: "#326377FF" }}>
        {uid ? "Update Clinic" : "Add New Clinic"}
      </Typography>
      <Box sx={{ height: "20px" }}></Box>
      <Grid container spacing={2}>
        {/* General Information Fields in a Grid Layout */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label="Name"
            name="name"
            value={clinicData.name}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label="Address"
            name="address"
            value={clinicData.address}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label="Alternate Phone"
            name="altphone"
            value={clinicData.altphone}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Description"
            name="description"
            value={clinicData.description}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label="Country"
            name="country"
            value="India"
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label="State"
            name="state"
            value="West Bengal"
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label="District"
            name="district"
            value={clinicData.district}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label="Phone"
            name="phone"
            value={clinicData.phone}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label="Pincode"
            name="pincode"
            value={clinicData.pincode}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            type="number"
            label="Rating"
            name="rating"
            value={clinicData.rating}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label="Registration No"
            name="regNo"
            value={clinicData.regNo}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label="Redirect URL"
            name="redirectUrl"
            value={clinicData.redirectUrl}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label="Image URL"
            name="imgurl"
            value={clinicData.imgurl}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12}>
          <IconButton
            onClick={() => setOpenModal(true)}
            color="primary"
            aria-label="upload image"
          >
            <PhotoCamera />
          </IconButton>
        </Grid>
      </Grid>

      {/* Operating Hours Section */}
      <Box
        sx={{
          backgroundColor: "#F1F0FFFF",
          borderRadius: 4,
          padding: 3,
          marginTop: 3,
        }}
      >
        <Typography variant="h5" sx={{ marginTop: 3 }}>
          Operating Hours
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(clinicData.open_date_time).map(([day, timings]) => (
            <Grid item xs={12} sm={6} md={4} key={day}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="h6">{day}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    fullWidth
                    type="time"
                    label="First Half"
                    value={timings.firstHalf}
                    onChange={(e) =>
                      handleOpenDateTimeChange(day, "firstHalf", e.target.value)
                    }
                  />
                  <Checkbox
                    checked={timings.open}
                    onChange={() =>
                      handleOpenDateTimeChange(day, "open", !timings.open)
                    }
                    inputProps={{ "aria-label": `${day} open checkbox` }}
                  />
                </Box>
                <TextField
                  fullWidth
                  type="time"
                  label="Second Half"
                  value={timings.secondHalf}
                  onChange={(e) =>
                    handleOpenDateTimeChange(day, "secondHalf", e.target.value)
                  }
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Facility Type Section */}
      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Facility Type
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={clinicData.is_clinic}
                  onChange={() => handleToggle("is_clinic")}
                  color="primary"
                />
              }
              label="Clinic"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={clinicData.is_hospital}
                  onChange={() => handleToggle("is_hospital")}
                  color="primary"
                />
              }
              label="Hospital"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={clinicData.is_lab}
                  onChange={() => handleToggle("is_lab")}
                  color="primary"
                />
              }
              label="Laboratory"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={clinicData.is_diagnostic}
                  onChange={() => handleToggle("is_diagnostic")}
                  color="primary"
                />
              }
              label="Diagnostic Center"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={clinicData.is_online}
                  onChange={() => handleToggle("is_online")}
                  color="primary"
                />
              }
              label="Online Consultation"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={clinicData.is_oxygen}
                  onChange={() => handleToggle("is_oxygen")}
                  color="primary"
                />
              }
              label="Oxygen Provider"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Submit Button */}
      <Button
        variant="contained"
        color="primary"
        onClick={uid ? handleUpdateClinic : handleAddClinic}
        sx={{ mt: 2 }}
      >
        {uid ? "Update Clinic" : "Add Clinic"}
      </Button>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <div>
        <DynamicUploadModal
          open={openModal}
          onClose={handleModalClose}
          uploadPreset="bookMyDr" // Custom Cloudinary upload preset
          folderName="Madecare" // Custom folder name
          maxSize={5 * 1024 * 1024} // Custom max file size: 5MB
          onUploadSuccess={handleUploadSuccess} // Callback after success
        />
      </div>
    </Box>
  );
};

export default AddNewClinic;